import { SUBSCRIPTION_RUNTIME_TYPES, type RuntimeProfile, type RuntimeUsageWindow } from '@deskworks/shared-types';
import { useQueryClient } from '@tanstack/react-query';
import { Gauge } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { formatAbsolute, formatRelative } from '@/lib/format';
import { useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { formatTokens, formatUsd, monthlyBudget, totalTokens } from '@/lib/usage';
import { cn } from '@/lib/utils';

/**
 * What's left on a runtime: the plan windows a subscription reported on its
 * latest run, and this month's usage (with the budget of an API runtime).
 */
export function RuntimeUsageSummary({ profile: p }: { profile: RuntimeProfile }) {
  if (p.runtimeType === 'local') return null;
  const subscription = SUBSCRIPTION_RUNTIME_TYPES.includes(p.runtimeType);
  const month = p.monthUsage;
  const budget = subscription ? null : monthlyBudget(p.config);
  const limits = p.usageLimits;

  return (
    <div className="mt-2 grid gap-2 text-xs">
      {subscription &&
        (limits && limits.windows.length > 0 ? (
          <div className="grid gap-1.5 sm:max-w-md">
            {limits.windows.map((w) => (
              <WindowMeter key={w.key} window={w} />
            ))}
            <span className="text-muted-foreground" title={formatAbsolute(p.usageLimitsAt, true)}>
              {limits.plan && <span className="capitalize">{t('{plan} plan', { plan: limits.plan })} · </span>}
              {limits.credits && (
                <span>{limits.credits.unlimited ? `${t('unlimited credits')} · ` : limits.credits.balance != null ? `${t('{n} credits left', { n: limits.credits.balance })} · ` : ''}</span>
              )}
              {t('as of {time}', { time: formatRelative(p.usageLimitsAt) })}
            </span>
          </div>
        ) : (
          <span className="text-muted-foreground">
            {p.hasCredential ? t('How much of the plan is left shows up here after a check or the first agent run.') : t('Connect the runtime to see its plan limits.')}
          </span>
        ))}
      {subscription && p.hasCredential && <CheckUsage profile={p} />}
      {budget !== null && month && <Meter label={t('Monthly budget')} used={month.apiCostUsd} limit={budget} />}
      {month && month.runs > 0 ? (
        <span className="text-muted-foreground">
          {month.runs === 1
            ? t('This month: {tokens} tokens in 1 run', { tokens: formatTokens(totalTokens(month)) })
            : t('This month: {tokens} tokens in {n} runs', { tokens: formatTokens(totalTokens(month)), n: month.runs })}
          {subscription
            ? month.subscriptionValueUsd > 0 && ` · ${t('worth {usd} at API prices (covered by the plan)', { usd: formatUsd(month.subscriptionValueUsd) })}`
            : ` · ${
                budget !== null
                  ? t('{spent} spent of {budget}', { spent: formatUsd(month.apiCostUsd), budget: formatUsd(budget) })
                  : t('{spent} spent', { spent: formatUsd(month.apiCostUsd) })
              }`}
          {month.unpricedRuns > 0 && ` · ${t('{n} without a known price', { n: month.unpricedRuns })}`}
        </span>
      ) : (
        <span className="text-muted-foreground">{t('No runs this month yet.')}</span>
      )}
    </div>
  );
}

/** "Check usage": a short call through the CLI that reports the plan limits (docs/features/ai-usage.md). */
function CheckUsage({ profile: p }: { profile: RuntimeProfile }) {
  const queryClient = useQueryClient();
  const check = useApiMutation(() => api.runtimeProfiles.checkUsage(p.id), {
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['ws'] }),
  });
  const state = p.usageCheck;
  const running = state?.status === 'running' || check.isPending;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button size="xs" variant="outline" onClick={() => check.mutate()} loading={running} title={t('Asks the CLI for a one-word answer and reads the plan limits it reports. Uses a tiny bit of the plan.')}>
        <Gauge /> {running ? t('Checking …') : t('Check usage')}
      </Button>
      {state?.status === 'failed' && !running && <span className="text-amber-700 dark:text-amber-400">{t('Check failed: {message}', { message: state.message ?? '' })}</span>}
    </div>
  );
}

function WindowMeter({ window: w }: { window: RuntimeUsageWindow }) {
  const resetPassed = w.resetsAt ? new Date(w.resetsAt).getTime() < Date.now() : false;
  const used = resetPassed ? 0 : w.usedPercent;
  return (
    <div>
      <div className="mb-0.5 flex items-baseline justify-between gap-2">
        <span className="font-medium text-foreground">{w.label}</span>
        <span className="text-muted-foreground tabular-nums">
          {resetPassed ? (
            t('reset since the last run')
          ) : (
            <>
              {t('{n}% left', { n: Math.max(0, Math.round(100 - used)) })}
              {w.resetsAt && <span title={formatAbsolute(w.resetsAt)}> · {t('resets {time}', { time: formatRelative(w.resetsAt) })}</span>}
            </>
          )}
        </span>
      </div>
      <Bar percent={used} />
    </div>
  );
}

function Meter({ label, used, limit }: { label: string; used: number; limit: number }) {
  const percent = (used / limit) * 100;
  return (
    <div className="sm:max-w-md">
      <div className="mb-0.5 flex items-baseline justify-between gap-2">
        <span className="font-medium text-foreground">{label}</span>
        <span className="text-muted-foreground tabular-nums">
          {t('{left} left of {limit}', { left: formatUsd(Math.max(0, limit - used)), limit: formatUsd(limit) })}
        </span>
      </div>
      <Bar percent={percent} />
    </div>
  );
}

/** Used share of an allowance; turns amber from 75 % and red from 90 % (the text beside it carries the number). */
function Bar({ percent }: { percent: number }) {
  const clamped = Math.min(100, Math.max(0, percent));
  return (
    <div
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(clamped)}
      className="h-1.5 overflow-hidden rounded-full bg-muted"
    >
      <div
        className={cn(
          'h-full rounded-full',
          clamped >= 90 ? 'bg-red-500' : clamped >= 75 ? 'bg-amber-500' : 'bg-indigo-500 dark:bg-indigo-400',
        )}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}
